import React, { useContext, useEffect, useState, useCallback } from 'react';
import {
  Box, Typography, Paper, Checkbox, Radio, RadioGroup,
  FormControlLabel, FormGroup, Button, CircularProgress, Divider,
} from '@mui/material';
import Swal from 'sweetalert2';
import { useQueryClient } from '@tanstack/react-query';
import api from '../adapters/api';
import { AuthContext } from '../context/AuthContext';
import DesktopNotificationPrompt from '../components/DesktopNotificationPrompt';

const CHANNEL_LABELS = {
  credit:    'เครดิต',
  online:    'ออนไลน์',
  'walk-in': 'หน้าร้าน',
};
const ALL_CHANNELS = Object.keys(CHANNEL_LABELS);

function buildInitialState(subscriptions) {
  const state = {};
  for (const ch of ALL_CHANNELS) {
    state[ch] = { subscribed: false, entireChannel: true, selectedUsers: new Set() };
  }
  for (const sub of subscriptions) {
    const { channel, entire_channel, target_user_code } = sub;
    if (!state[channel]) continue;
    state[channel].subscribed = true;
    if (!entire_channel && target_user_code) {
      state[channel].entireChannel = false;
      state[channel].selectedUsers.add(target_user_code);
    } else {
      state[channel].entireChannel = true;
    }
  }
  return state;
}

export default function NotificationSettings() {
  const { currentUser } = useContext(AuthContext);
  const qc = useQueryClient();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [channels, setChannels] = useState({});
  const [subState, setSubState] = useState({});

  const isThurakan = currentUser?.dept?.some(d => d.includes('ธุรการ')) || currentUser?.user_level >= 2;

  const load = useCallback(async () => {
    try {
      const { data } = await api.get('/api/subscriptions');
      setChannels(data.channels || {});
      setSubState(buildInitialState(data.subscriptions || []));
    } catch (err) {
      console.error('load subscriptions error:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const setChannel = (ch, patch) => setSubState(prev => ({
    ...prev,
    [ch]: { ...prev[ch], ...patch },
  }));

  const toggleUserSelection = (ch, userCode) => {
    setSubState(prev => {
      const selected = new Set(prev[ch].selectedUsers);
      if (selected.has(userCode)) selected.delete(userCode);
      else selected.add(userCode);
      return { ...prev, [ch]: { ...prev[ch], selectedUsers: selected } };
    });
  };

  const handleSave = async () => {
    for (const ch of ALL_CHANNELS) {
      const s = subState[ch];
      if (s?.subscribed && !s.entireChannel && s.selectedUsers.size === 0) {
        Swal.fire({
          icon: 'warning',
          title: 'กรุณาเลือกรายชื่อ',
          text: `เลือก "เลือกรายคน" สำหรับช่องทาง "${CHANNEL_LABELS[ch]}" แต่ยังไม่ได้เลือกรายชื่อ กรุณาเลือกอย่างน้อย 1 คน หรือเปลี่ยนเป็น "ทั้งแผนก"`,
          confirmButtonColor: '#1976d2',
          confirmButtonText: 'ตกลง',
        });
        return;
      }
    }

    const rows = [];
    for (const ch of ALL_CHANNELS) {
      const s = subState[ch];
      if (!s?.subscribed) continue;
      if (s.entireChannel) {
        rows.push({ channel: ch, entire_channel: true, target_user_code: null });
      } else {
        for (const uc of s.selectedUsers) {
          rows.push({ channel: ch, entire_channel: false, target_user_code: uc });
        }
      }
    }

    setSaving(true);
    try {
      await api.put('/api/subscriptions', rows);
      // insight ของธุรการนับตามเซลล์ที่ติดตาม — server สร้างของวันนี้ใหม่ให้แล้ว จึงโหลดการ์ด/KPI/ป้ายใหม่ทันที
      qc.invalidateQueries({ predicate: q => String(q.queryKey[0]).startsWith('insight') });
      Swal.fire({ icon: 'success', title: 'บันทึกการตั้งค่าสำเร็จ', timer: 1500, showConfirmButton: false });
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'เกิดข้อผิดพลาด', text: 'ไม่สามารถบันทึกการตั้งค่าได้', confirmButtonColor: '#1976d2' });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!isThurakan) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <Typography variant="h6" color="text.secondary">
          หน้านี้สำหรับแผนกธุรการเท่านั้น
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 560, mx: 'auto', p: { xs: 2, sm: 4 } }}>
      <Typography variant="h6" fontWeight={700} gutterBottom>
        ตั้งค่าการแจ้งเตือน Draft
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        เลือกช่องทางและ/หรือรายบุคคลที่ต้องการรับแจ้งเตือนเมื่อมีการบันทึก Draft ใหม่
      </Typography>

      <Box sx={{ mb: 3 }}>
        <DesktopNotificationPrompt />
      </Box>

      {ALL_CHANNELS.map(ch => {
        const s = subState[ch] || { subscribed: false, entireChannel: true, selectedUsers: new Set() };
        const users = channels[ch] || [];

        return (
          <Paper key={ch} variant="outlined" sx={{ mb: 2, borderRadius: 2, overflow: 'hidden' }}>
            {/* Channel header */}
            <Box sx={{ px: 2, py: 1.5, bgcolor: 'grey.50', display: 'flex', alignItems: 'center' }}>
              <Checkbox
                checked={s.subscribed}
                onChange={e => setChannel(ch, { subscribed: e.target.checked })}
                sx={{ p: 0.5, mr: 1 }}
              />
              <Typography fontWeight={600}>{CHANNEL_LABELS[ch]}</Typography>
              {users.length > 0 && (
                <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                  ({users.length} คน)
                </Typography>
              )}
            </Box>

            {s.subscribed && (
              <>
                <Divider />
                <Box sx={{ px: 3, py: 1.5 }}>
                  <RadioGroup
                    value={s.entireChannel ? 'all' : 'individual'}
                    onChange={e => setChannel(ch, { entireChannel: e.target.value === 'all' })}
                  >
                    <FormControlLabel value="all" control={<Radio size="small" />} label="ทั้งแผนก" />
                    <FormControlLabel value="individual" control={<Radio size="small" />} label="เลือกรายคน" />
                  </RadioGroup>

                  {!s.entireChannel && users.length > 0 && (
                    <FormGroup sx={{ pl: 3, mt: 0.5 }}>
                      {users.map(u => (
                        <FormControlLabel
                          key={u.user_code}
                          control={
                            <Checkbox
                              size="small"
                              checked={s.selectedUsers.has(u.user_code)}
                              onChange={() => toggleUserSelection(ch, u.user_code)}
                            />
                          }
                          label={<Typography variant="body2">{u.user_name}</Typography>}
                        />
                      ))}
                    </FormGroup>
                  )}

                  {!s.entireChannel && users.length === 0 && (
                    <Typography variant="body2" color="text.secondary" sx={{ pl: 3, mt: 0.5 }}>
                      ไม่พบรายชื่อในช่องทางนี้
                    </Typography>
                  )}
                </Box>
              </>
            )}
          </Paper>
        );
      })}

      <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={saving}
          sx={{ textTransform: 'none', px: 3 }}
        >
          {saving ? 'กำลังบันทึก…' : 'บันทึก'}
        </Button>
      </Box>
    </Box>
  );
}
